<template>
  <div class="mt-4 px-5">
    <h2>Arbeitsstunden Übersicht</h2>

    <v-alert dense type="info">
      <b>Info:</b>
      <div>
        Hier siehst du deine erfassten Arbeitsstunden. <br>
        Einträge müssen eingereicht werden, damit der Verein diese bestätigen kann.
      </div>
    </v-alert>

    <v-data-table
        :headers="headers"
        :items="$store.state.UserApp.WorkingHours"
        :sort-by="['beginn']"
        :sort-desc="[true]"
        class="elevation-1 mt-8"
        :loading="loading"
        :item-class="itemRowClass"
        loader-height="5"
        no-data-text="Keine Einträge vorhanden"
        loading-text="Einträge werden geladen..."
    >
      <template v-slot:top>
        <v-toolbar flat dense color="success" dark>
          <v-toolbar-title>Einträge</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="loadWorkingHours">
            <v-icon>mdi-reload</v-icon>
          </v-btn>
        </v-toolbar>

        <div style="padding: 12px 16px; border-bottom: 1px solid rgba(0,0,0,.12);">
          <v-row dense align="center">
            <v-col cols="12" sm="4" md="3">
              <v-select
                  v-model="filters.club_id"
                  :items="comp_Clubs"
                  item-text="name"
                  item-value="id"
                  label="Verein filtern"
                  outlined dense hide-details clearable
                  @change="loadWorkingHours"
              ></v-select>
            </v-col>
            <v-col cols="6" sm="3" md="2">
              <v-text-field
                  v-model="filters.inputDatevon"
                  label="Von"
                  type="date"
                  outlined dense hide-details
                  @change="loadWorkingHours"
              ></v-text-field>
            </v-col>
            <v-col cols="6" sm="3" md="2">
              <v-text-field
                  v-model="filters.inputDatebis"
                  label="Bis"
                  type="date"
                  outlined dense hide-details
                  @change="loadWorkingHours"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="auto" class="d-flex align-center">
              <v-btn text small @click="resetFilters">
                <v-icon left small>mdi-filter-off</v-icon>
                Zurücksetzen
              </v-btn>
            </v-col>
          </v-row>
        </div>
      </template>

      <template v-slot:item.beginn="{ item }">
        {{ item.BeginFormat }}
      </template>
      <template v-slot:item.ende="{ item }">
        {{ item.EndeFormat }}
      </template>

      <template v-slot:item.status="{ item }">
        <v-chip :color="getStatusColor(item.bestaetigung_status)" small dark>
          {{ getStatusText(item.bestaetigung_status) }}
        </v-chip>
      </template>

      <template v-slot:item.action="{ item }">
        <v-btn
            v-if="item.bestaetigung_status === 0"
            color="info"
            small
            @click="submitConfirmation(item)"
        >
          <v-icon left small>fas fa-hands-helping</v-icon>
          einreichen
        </v-btn>
        <v-btn
            v-if="item.bestaetigung_status === 1"
            color="warning"
            small
            @click="submitConfirmation(item)"
        >
          <v-icon left small>fas fa-thumbtack</v-icon>
          zurücknehmen
        </v-btn>
        <div v-if="item.bestaetigung_status === 3">
          <v-tooltip bottom v-if="item.abgelehnt_grund">
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on" class="error--text caption mr-2">
                <v-icon x-small color="error">mdi-alert-circle</v-icon> Grund
              </span>
            </template>
            <span>{{ item.abgelehnt_grund }}</span>
          </v-tooltip>
          <v-btn
              color="error"
              small
              @click="submitConfirmation(item)"
          >
            <v-icon left small>fas fa-redo</v-icon>
            Neu einreichen
          </v-btn>
        </div>
      </template>
    </v-data-table>
  </div>
</template>

<script>
export default {
  name: "WorkingHoursList",
  data: () => ({
    loading: false,
    filters: {
      club_id: null,
      inputDatevon: null,
      inputDatebis: null,
    },
    headers: [
      { text: 'Verein', value: 'club_name' },
      { text: 'Tätigkeit', value: 'funktion_name' },
      { text: 'Einsatz', value: 'arbeitseinsatz_titel' },
      { text: 'Beginn', value: 'beginn' },
      { text: 'Ende', value: 'ende' },
      { text: 'Status', value: 'status', sortable: false },
      { text: 'Aktion', value: 'action', sortable: false, align: 'center' },
    ],
  }),
  computed: {
    comp_Clubs() {
      const clubsData = this.$store.state.UserApp.Clubs;
      if (!clubsData) return [];
      
      const allClubs = clubsData.meineVereine || clubsData.Vereine || [];
      let filtered = allClubs.filter(c => (c.can && (c.can.arbeitsstunden || c.can.arbeitsstunden_full)) || c.has_arbeitsstunden || c.has_arbeitsstunden_full || c.hasArbeitsstundenAccess);
      
      if (filtered.length === 0 && allClubs.length > 0) {
        filtered = allClubs;
      }
      return filtered;
    },
  },
  methods: {
    async loadWorkingHours() {
      this.loading = true;
      try {
        await this.$store.dispatch('UserApp/getUserWorkingHours', this.filters);
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
    resetFilters() {
      this.filters = {
        club_id: null,
        inputDatevon: null,
        inputDatebis: null,
      };
      this.loadWorkingHours();
    },
    getStatusText(status) {
      const map = {
        0: 'Nicht eingereicht',
        1: 'Eingereicht',
        2: 'Bestätigt',
        3: 'Abgelehnt'
      };
      return map[status] || 'Unbekannt';
    },
    getStatusColor(status) {
      const map = {
        0: 'grey',
        1: 'info',
        2: 'success',
        3: 'error'
      };
      return map[status] || 'grey';
    },
    itemRowClass(item) {
      if (item.bestaetigung_status === 2) return 'green lighten-5';
      if (item.bestaetigung_status === 1) return 'blue lighten-5';
      if (item.bestaetigung_status === 3) return 'red lighten-5';
      return '';
    },
    async submitConfirmation(item) {
      const data = {
        freigabeID: item.id,
        freigabebool: item.bestaetigung_status !== 1,
      };
      try {
        await this.$store.dispatch('UserApp/send_WorkingHoursRecord_to_confirmation', data);
        this.loadWorkingHours();
      } catch (error) {
        console.error(error);
      }
    }
  },
  mounted() {
    this.loadWorkingHours();
  }
};
</script>
